/*
 * <auction-list> — drives the auctions listing page (sections/auction-list.liquid):
 * the live grid, a "Trending now" row, a "Your watched lots" row, free-text search,
 * a sort control, and the existing category filter buttons.
 *
 * Each child <auction-card> is server-rendered with its product image/title/link
 * and a data-state-url pointing at the auction API. This component fetches every
 * card's live state (current bid, bid/watcher counts, end time / end-state, and
 * whether the current visitor is watching it) in one batched request and keeps it
 * updated on an interval, pausing while the tab is hidden.
 *
 * Reuses the <auction-timer> custom element and the money/token helpers defined in
 * assets/auction.js (loaded before this file), so countdown + money formatting stay
 * consistent with the product-page auction UI.
 */
if (!customElements.get('auction-card')) {
  customElements.define(
    'auction-card',
    class AuctionCard extends HTMLElement {
      constructor() {
        super();
        this.bidEl = this.querySelector('[data-auction-card-bid]');
        this.metaEl = this.querySelector('[data-auction-card-meta]');
        this.timerEl = this.querySelector('[data-auction-card-timer]');
        this.ctaEl = this.querySelector('.auction-card__cta');
        this.bidRowEl = this.querySelector('.auction-card__bid-row');
        this.endStateEl = this.querySelector('[data-auction-card-end-state]');
        this.watchButton = this.querySelector('[data-auction-card-watch]');
        this.lotNumberEl = this.querySelector('[data-auction-card-lot-number]');

        this.lotNumberTemplate = this.querySelector('[data-auction-card-lot-number-template]').textContent;
        this.bidCountTemplate = this.querySelector('[data-auction-card-bid-count-template]').textContent;
        this.watchingTemplate = this.querySelector('[data-auction-card-watching-template]').textContent;
        this.soldTemplate = this.querySelector('[data-auction-card-sold-template]').textContent;
        this.endedLabel = this.querySelector('[data-auction-card-ended-label]').textContent;

        this.onWatchClick = this.onWatchClick.bind(this);
        this.onTimerExpired = this.onTimerExpired.bind(this);
      }

      connectedCallback() {
        if (this.watchButton) this.watchButton.addEventListener('click', this.onWatchClick);
        // Without this, the card only learns an auction ended on the next
        // regular poll - refetch the moment its own countdown hits zero so
        // the "live" badge doesn't linger after the timer already shows 00:00:00.
        this.addEventListener('auction:timer-expired', this.onTimerExpired);
      }

      disconnectedCallback() {
        if (this.watchButton) this.watchButton.removeEventListener('click', this.onWatchClick);
        this.removeEventListener('auction:timer-expired', this.onTimerExpired);
      }

      onTimerExpired() {
        this.fetchState();
      }

      onWatchClick(evt) {
        // The card is wrapped in a link to the product page - don't navigate.
        evt.preventDefault();
        evt.stopPropagation();

        fetch(AuctionIdentity.withToken(this.dataset.watchUrl), {
          method: 'POST',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        })
          .then((response) => response.json())
          .then((data) => {
            AuctionIdentity.set(data.bidderToken);
            this.watchButton.setAttribute('aria-pressed', String(data.watching));
            this.watchButton.classList.toggle('is-watching', data.watching);
            if (this.lastState) {
              this.lastState.watcherCount = data.watcherCount;
              this.lastState.isWatching = data.watching;
              this.renderMeta(this.lastState);
            }
            this.dispatchEvent(new CustomEvent('auction-card:watch-changed', { bubbles: true }));
          })
          .catch((e) => console.error(e));
      }

      fetchState() {
        return fetch(AuctionIdentity.withToken(this.dataset.stateUrl), {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        })
          .then((response) => response.json())
          .then((data) => this.render(data))
          .catch((e) => console.error(e));
      }

      /*
       * Render from a batch LotSummary (GET /lots). The summary omits the
       * per-bidder fields (bidHistory) that the per-lot state endpoint returns.
       */
      renderFromSummary(summary) {
        this.render(summary);
      }

      /** Popularity score used for the "Trending now" row - simple, not scientific. */
      score() {
        if (!this.lastState) return 0;
        return (this.lastState.bidCount || 0) * 3 + (this.lastState.watcherCount || 0) * 2;
      }

      render(data) {
        // Captured before this.lastState is overwritten below, so it still
        // reflects what was on screen a moment ago - used only to decide
        // whether to flash the price (see below), never read afterward.
        const previousBid = this.lastState ? this.lastState.currentBid : undefined;
        this.lastState = data;
        this.renderLotNumber(data);

        if (data.status !== 'live') {
          this.renderEnded(data);
          return;
        }

        this.classList.remove('auction-card--ended');
        this.endStateEl.hidden = true;
        // .auction-card__bid-row sets its own unconditional `display: flex`,
        // which beats the native [hidden] attribute's UA styling (author CSS
        // always outranks the UA stylesheet) - the .hidden class works
        // because base.css declares it !important.
        this.bidRowEl.classList.remove('hidden');
        // .hidden (the class, not the native attribute) - .auction-card__cta
        // also carries .button, which sets its own unconditional `display:
        // inline-flex` in base.css with no [hidden] override, so the same
        // cascade issue noted above for bidRowEl/watchButton applies here too.
        if (this.ctaEl) this.ctaEl.classList.remove('hidden');

        this.bidEl.textContent =
          data.currentBidFormatted || this.formatMoney(data.currentBid);

        // Quiet color flash when the price actually moves - never on the
        // first render (previousBid still undefined then) or an unchanged poll.
        if (previousBid != null && data.currentBid !== previousBid) {
          this.bidEl.classList.remove('auction-card__bid-value--updated');
          // eslint-disable-next-line no-void -- forces a reflow so re-adding
          // the class immediately after removing it still restarts the animation
          void this.bidEl.offsetWidth;
          this.bidEl.classList.add('auction-card__bid-value--updated');
        }

        this.renderMeta(data);

        if (this.watchButton) {
          // .hidden (the class, not the native attribute) - .auction-card__watch-button
          // sets its own unconditional `display`, which would otherwise beat the
          // browser's default [hidden] styling (author CSS always outranks the
          // UA stylesheet, regardless of specificity). base.css's .hidden carries
          // !important specifically so it can't lose that fight.
          this.watchButton.classList.remove('hidden');
          this.watchButton.setAttribute('aria-pressed', String(Boolean(data.isWatching)));
          this.watchButton.classList.toggle('is-watching', Boolean(data.isWatching));
        }

        if (this.timerEl && typeof this.timerEl.setEndsAt === 'function') {
          this.timerEl.setEndsAt(data.endsAt);
        }
      }

      renderMeta(data) {
        const bids = this.fill(this.bidCountTemplate, { COUNT: data.bidCount });
        const watching = this.fill(this.watchingTemplate, { COUNT: data.watcherCount });
        this.metaEl.hidden = false;
        this.metaEl.textContent = `${bids} · ${watching}`;
      }

      /** Backend-assigned, stable across other lots being added/removed - never
       *  recomputed from grid position the way it used to be. */
      renderLotNumber(data) {
        if (!this.lotNumberEl || typeof data.lotNumber !== 'number') return;
        const padded = String(data.lotNumber).padStart(2, '0');
        this.lotNumberEl.textContent = this.fill(this.lotNumberTemplate, { NUMBER: padded });
        this.lotNumberEl.hidden = false;
      }

      renderEnded(data) {
        this.classList.add('auction-card--ended');
        this.bidRowEl.classList.add('hidden');
        if (this.ctaEl) this.ctaEl.classList.add('hidden');
        this.endStateEl.hidden = false;

        // Watching only makes sense for a lot that could still change state -
        // a paused auction can resume, but sold/paid/passed never will, so
        // there's nothing left to watch for. Uses the .hidden class (not the
        // native attribute) - see the comment in render() above for why.
        if (this.watchButton) this.watchButton.classList.toggle('hidden', data.status !== 'paused');

        if (data.status === 'sold' || data.status === 'paid') {
          const amount = data.currentBidFormatted || this.formatMoney(data.currentBid);
          this.endStateEl.textContent = this.fill(this.soldTemplate, { AMOUNT: amount });
        } else if (data.status === 'paused') {
          this.endStateEl.textContent = this.querySelector('[data-auction-card-paused-label]')?.textContent || this.endedLabel;
        } else {
          this.endStateEl.textContent = this.endedLabel;
        }
      }

      formatMoney(amount) {
        if (typeof auctionFormatMoney === 'function' && typeof amount === 'number') {
          return auctionFormatMoney(Math.round(amount * 100), this.dataset.moneyFormat);
        }
        return amount == null ? '—' : String(amount);
      }

      fill(template, tokens) {
        if (typeof auctionFillTemplate === 'function') return auctionFillTemplate(template, tokens);
        let result = template;
        Object.keys(tokens).forEach((key) => {
          result = result.replace(`%%${key}%%`, tokens[key]);
        });
        return result;
      }
    }
  );
}

if (!customElements.get('auction-list')) {
  customElements.define(
    'auction-list',
    class AuctionList extends HTMLElement {
      constructor() {
        super();
        this.pollIntervalMs = (Number.parseInt(this.dataset.pollInterval, 10) || 4) * 1000;
        this.trendingCount = Number.parseInt(this.dataset.trendingCount, 10) || 0;
        this.pollTimer = null;
        this.searchTerm = '';
        this.sortMode = 'ending_soon';
        this.categoryFilter = '*';
        this.rarityFilter = '*';
        this.onVisibilityChange = this.onVisibilityChange.bind(this);
        this.onWatchChanged = this.onWatchChanged.bind(this);
      }

      connectedCallback() {
        this.gridEl = this.querySelector('[data-auction-grid]');
        this.loadingEl = this.querySelector('[data-auction-list-loading]');
        this.trendingSectionEl = this.querySelector('[data-auction-trending]');
        this.trendingTrackEl = this.querySelector('[data-auction-trending-track]');
        this.watchedSectionEl = this.querySelector('[data-auction-watched]');
        this.watchedTrackEl = this.querySelector('[data-auction-watched-track]');
        this.previewTemplateEl = this.querySelector('[data-auction-highlight-card-template]');
        this.searchInputEl = this.querySelector('[data-auction-search-input]');
        this.sortSelectEl = this.querySelector('[data-auction-sort-select]');
        this.rarityFilterSelect = this.querySelector('[data-auction-rarity-select]');
        this.noResultsEl = this.querySelector('[data-auction-no-results]');

        // The stats row lives in the section header, ABOVE (not inside) this
        // element, so it has to be looked up from the wider section scope.
        const section = this.closest('.auction-list-section') || this.parentElement;
        this.statsEl = section ? section.querySelector('[data-auction-stats]') : null;
        this.statLiveEl = section ? section.querySelector('[data-auction-stat-live]') : null;
        this.statWatchersEl = section ? section.querySelector('[data-auction-stat-watchers]') : null;

        this.cards = Array.from(this.gridEl.querySelectorAll('auction-card'));
        this.cardsById = new Map(this.cards.map((card) => [card.dataset.lotId, card]));
        this.listUrl = this.dataset.listUrl;
        this.endedListUrl = this.dataset.endedListUrl;
        this.statsUrl = this.dataset.statsUrl;

        this.poll();
        this.startPolling();
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        this.addEventListener('auction-card:watch-changed', this.onWatchChanged);
        this.setupFilters();
        this.setupSearch();
        this.setupSort();
        this.setupRarityFilter();
      }

      /** Runs everything a poll tick needs: per-lot state plus store-wide stats. */
      poll() {
        this.refreshAll();
        this.fetchStats();
      }

      disconnectedCallback() {
        this.stopPolling();
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.removeEventListener('auction-card:watch-changed', this.onWatchChanged);
      }

      setupFilters() {
        // Filter buttons live in the section, as a sibling of this element.
        const section = this.closest('.auction-list-section') || this.parentElement;
        this.filterBar = section ? section.querySelector('[data-auction-filters]') : null;
        if (!this.filterBar) return;

        this.filterButtons = Array.from(this.filterBar.querySelectorAll('[data-auction-filter]'));
        this.filterButtons.forEach((button) => {
          button.addEventListener('click', () => this.applyFilter(button));
        });
      }

      setupSearch() {
        if (!this.searchInputEl) return;
        let debounceTimer;
        this.searchInputEl.addEventListener('input', () => {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            this.searchTerm = this.searchInputEl.value.trim().toLowerCase();
            this.reorganize();
          }, 150);
        });
      }

      setupSort() {
        if (!this.sortSelectEl) return;
        this.sortSelectEl.addEventListener('change', () => {
          this.sortMode = this.sortSelectEl.value;
          this.reorganize();
        });
      }

      setupRarityFilter() {
        if (!this.rarityFilterSelect) return;
        this.rarityFilterSelect.addEventListener('change', () => {
          this.rarityFilter = this.rarityFilterSelect.value;
          this.reorganize();
        });
      }

      applyFilter(activeButton) {
        this.filterButtons.forEach((button) => {
          const isActive = button === activeButton;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', String(isActive));
        });
        this.categoryFilter = activeButton.dataset.auctionFilter;
        this.reorganize();
      }

      onWatchChanged() {
        // A watch toggle can move a card in/out of the watched row immediately,
        // without waiting for the next poll.
        this.reorganize();
      }

      fetchLots(url) {
        return fetch(AuctionIdentity.withToken(url), {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        })
          .then((response) => response.json())
          .then((data) => data?.lots || []);
      }

      refreshAll() {
        // Prefer batched requests when the section provides list URLs. Cards
        // are rendered from Shopify collection membership, not from "is this
        // lot still live" - a lot that ended stays in the DOM until the
        // merchant removes it from the collection, so it has to be covered by
        // *some* fetch or its card would freeze showing stale live data
        // forever (status=live alone would simply stop returning it at all).
        if (this.listUrl) {
          const requests = [this.fetchLots(this.listUrl)];
          if (this.endedListUrl) requests.push(this.fetchLots(this.endedListUrl));

          Promise.all(requests)
            .then((results) => {
              const lots = results.flat();
              lots.forEach((lot) => {
                const card = this.cardsById.get(String(lot.id));
                if (card) card.renderFromSummary(lot);
              });
              // A card exists purely because its product is in the collection
              // with auction.enabled=true - Liquid has no way to know whether
              // its auction has actually started yet (e.g. a future startsAt,
              // excluded from both the live and ended lists on the API side).
              // Without this, that card just sits at its default placeholder
              // content forever, since nothing above ever touches it. Checked
              // in placeInGrid() alongside the other filters.
              this.knownLotIds = new Set(lots.map((lot) => String(lot.id)));
              this.reorganize();
              this.revealGrid();
            })
            .catch(() => {
              // Fall back to per-card fetches if the batch requests fail. That
              // endpoint has no "not found" signal the same way (a direct
              // per-lot lookup, not a filtered list), so disable the
              // not-yet-started concealment above rather than risk hiding
              // cards based on a stale knownLotIds from an earlier success.
              this.knownLotIds = null;
              Promise.all(this.cards.map((card) => card.fetchState())).then(() => {
                this.reorganize();
                this.revealGrid();
              });
            });
          return;
        }

        this.knownLotIds = null;
        Promise.all(this.cards.map((card) => card.fetchState())).then(() => {
          this.reorganize();
          this.revealGrid();
        });
      }

      /**
       * Swaps the initial loading spinner for the real grid - idempotent, so
       * it's safe to call after every poll rather than just the first. Runs
       * regardless of how many cards actually exist, so an empty collection
       * (0 auction-enabled products) still clears the spinner instead of
       * leaving it spinning forever - reorganize() alone can't be used for
       * this, since it bails out early when there's nothing to reorganize.
       */
      revealGrid() {
        if (this.loadingEl) this.loadingEl.hidden = true;
        // .hidden (the class, not the native attribute) - .auction-list sets
        // its own unconditional `display: grid`, which would otherwise beat
        // the browser's default [hidden] styling (author CSS always outranks
        // the UA stylesheet, regardless of specificity).
        if (this.gridEl) this.gridEl.classList.remove('hidden');
      }

      /*
       * A lot never leaves the main grid just because it's watched or trending -
       * it stays exactly where a shopper first saw it, sorted/filtered/searched
       * like every other card. "Trending"/"Watched" are just quick-access PREVIEW
       * rows built from the same already-fetched data. Re-run after every poll
       * and on every search/sort/filter/watch-toggle change.
       */
      reorganize() {
        // Bail only if NEITHER signal exists yet: no card has real state, and
        // no poll has even completed (knownLotIds, set in refreshAll(), even
        // if it turns out empty). Without the second half, a collection where
        // EVERY lot is not-yet-started would never reach placeInGrid() below
        // - since no card ever gets real lastState - leaving every card stuck
        // visible instead of correctly hidden via the isKnown check there.
        if (!this.cards.some((card) => card.lastState) && !this.knownLotIds) return;

        const withState = this.cards.filter((card) => card.lastState);

        const watched = withState
          .filter((card) => card.lastState.isWatching)
          .sort((a, b) => Date.parse(a.lastState.endsAt || 0) - Date.parse(b.lastState.endsAt || 0));

        const watchedIds = new Set(watched.map((card) => card.dataset.lotId));

        const trending =
          this.trendingCount > 0
            ? withState
                .filter((card) => card.lastState.status === 'live' && !watchedIds.has(card.dataset.lotId) && card.score() > 0)
                .sort((a, b) => b.score() - a.score())
                .slice(0, this.trendingCount)
            : [];

        this.placeInTrack(this.trendingSectionEl, this.trendingTrackEl, trending.length >= 2 ? trending : []);
        this.placeInTrack(this.watchedSectionEl, this.watchedTrackEl, watched);
        this.placeInGrid(this.cards);
      }

      /** Renders lightweight previews - never moves the real card out of the grid. */
      placeInTrack(sectionEl, trackEl, cards) {
        if (!sectionEl || !trackEl || !this.previewTemplateEl) return;
        sectionEl.hidden = cards.length === 0;
        trackEl.replaceChildren(...cards.map((card) => this.buildPreview(card)));
      }

      buildPreview(card) {
        const node = this.previewTemplateEl.content.firstElementChild.cloneNode(true);
        const sourceLink = card.querySelector('.auction-card__link');
        const sourceImage = card.querySelector('.auction-card__image');
        const sourceTitle = card.querySelector('.auction-card__title');

        if (sourceLink) node.href = sourceLink.href;

        const image = node.querySelector('.auction-highlight-card__image');
        if (sourceImage && image) {
          image.src = sourceImage.currentSrc || sourceImage.src;
          image.alt = sourceImage.alt || '';
        }

        node.querySelector('.auction-highlight-card__title').textContent = sourceTitle ? sourceTitle.textContent : '';
        node.querySelector('.auction-highlight-card__bid').textContent =
          card.lastState.currentBidFormatted || card.formatMoney(card.lastState.currentBid);

        const bids = card.fill(card.bidCountTemplate, { COUNT: card.lastState.bidCount });
        const watching = card.fill(card.watchingTemplate, { COUNT: card.lastState.watcherCount });
        node.querySelector('.auction-highlight-card__meta').textContent = `${bids} · ${watching}`;

        // Delegates the actual toggle to the real card's own watch button, so
        // the fetch/state-update/re-render logic lives in exactly one place
        // (AuctionCard.onWatchClick) - this preview never duplicates it.
        const watchButton = node.querySelector('[data-auction-highlight-card-watch]');
        if (watchButton) {
          // "Your watched lots" deliberately keeps showing a lot after it ends
          // (so you can see the outcome), but there's nothing left to watch
          // for once it's actually over - a paused auction can still resume.
          // Uses the .hidden class (not the native attribute) - see the
          // comment in AuctionCard.render() above for why.
          const canWatch = card.lastState.status === 'live' || card.lastState.status === 'paused';
          watchButton.classList.toggle('hidden', !canWatch);
          const isWatching = Boolean(card.lastState.isWatching);
          watchButton.setAttribute('aria-pressed', String(isWatching));
          watchButton.classList.toggle('is-watching', isWatching);
          watchButton.addEventListener('click', (evt) => {
            evt.preventDefault();
            evt.stopPropagation();
            if (card.watchButton) card.watchButton.click();
          });
        }

        return node;
      }

      placeInGrid(cards) {
        const sorted = this.sortCards(cards.slice());
        let visibleCount = 0;

        // appendChild() on a node that already has a parent always does a real
        // remove-then-reinsert, even when the target position is unchanged -
        // that cascades disconnectedCallback/connectedCallback to every custom
        // element in the moved subtree (including each card's <auction-timer>),
        // so only touch the DOM when the order actually needs to change.
        const currentOrder = Array.from(this.gridEl.children);
        const orderChanged = sorted.length !== currentOrder.length || sorted.some((card, i) => card !== currentOrder[i]);
        if (orderChanged) {
          sorted.forEach((card) => this.gridEl.appendChild(card));
        }

        sorted.forEach((card) => {
          const matchesCategory = this.categoryFilter === '*' || card.dataset.productType === this.categoryFilter;
          const matchesSearch = !this.searchTerm || (card.dataset.lotTitle || '').toLowerCase().includes(this.searchTerm);
          const matchesRarity = this.rarityFilter === '*' || this.cardRarityTier(card) === this.rarityFilter;
          // Absent from both the live and ended lists - most commonly a lot
          // whose startsAt hasn't arrived yet (see refreshAll()). null
          // knownLotIds means the per-card fallback fetch is in effect, which
          // has no such signal - treat every card as known in that case.
          const isKnown = !this.knownLotIds || this.knownLotIds.has(card.dataset.lotId);
          const visible = matchesCategory && matchesSearch && matchesRarity && isKnown;
          card.hidden = !visible;
          if (visible) visibleCount += 1;
        });

        if (this.noResultsEl) this.noResultsEl.hidden = sorted.length === 0 || visibleCount > 0;
      }

      /** Reads the tier already computed server-side by snippets/rarity-badge.liquid,
       *  rather than re-deriving it from the raw decimal metafield in JS. Returns ''
       *  for a lot with no rarity set at all (never matches a specific tier filter). */
      cardRarityTier(card) {
        const badge = card.querySelector('.rarity-badge');
        return badge ? badge.dataset.rarityTier : '';
      }

      sortCards(cards) {
        const isEnded = (card) => Boolean(card.lastState) && card.lastState.status !== 'live';
        const byScore = (card) => card.score();
        const bidCents = (card) => (card.lastState ? Math.round((card.lastState.currentBid || 0) * 100) : 0);
        const endsAt = (card) => Date.parse((card.lastState && card.lastState.endsAt) || 0) || Infinity;

        const compareWithinGroup = (a, b) => {
          switch (this.sortMode) {
            case 'most_watched':
              return (b.lastState.watcherCount || 0) - (a.lastState.watcherCount || 0);
            case 'most_bids':
              return (b.lastState.bidCount || 0) - (a.lastState.bidCount || 0);
            case 'price_low':
              return bidCents(a) - bidCents(b);
            case 'price_high':
              return bidCents(b) - bidCents(a);
            case 'ending_soon':
            default:
              return endsAt(a) - endsAt(b) || byScore(b) - byScore(a);
          }
        };

        // Ended lots always sink to the bottom regardless of sort mode - the
        // merchant removes them from the collection manually, so until then
        // they'd otherwise sit mixed in among genuinely live lots.
        return cards.sort((a, b) => {
          const endedDiff = Number(isEnded(a)) - Number(isEnded(b));
          return endedDiff !== 0 ? endedDiff : compareWithinGroup(a, b);
        });
      }

      /*
       * Store-wide totals from a dedicated endpoint (GET /stats), not summed
       * client-side from whatever's on this page - this section's grid may only
       * show one collection, but the header stat row should reflect every
       * auction in the store.
       */
      fetchStats() {
        if (!this.statsEl || !this.statsUrl) return;

        return fetch(this.statsUrl, {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        })
          .then((response) => response.json())
          .then((data) => {
            this.statLiveEl.textContent = String(data.liveCount ?? 0);
            this.statWatchersEl.textContent = String(data.totalWatchers ?? 0);
            this.statsEl.hidden = false;
          })
          .catch((e) => console.error(e));
      }

      startPolling() {
        if (this.pollTimer) return;
        this.pollTimer = setInterval(this.poll.bind(this), this.pollIntervalMs);
      }

      stopPolling() {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }

      onVisibilityChange() {
        if (document.hidden) {
          this.stopPolling();
        } else {
          this.poll();
          this.startPolling();
        }
      }
    }
  );
}
